/**
 * Hook para keyboard shortcuts de Undo/Redo
 * 
 * @module hooks/useUndoRedoShortcuts
 * @description Implementa Ctrl+Z (undo) e Ctrl+Shift+Z (redo)
 * para o sistema de DRE editável
 */

import { useEffect } from 'react';
import { useDREStore } from '../stores/dreStore';

/**
 * Hook que adiciona listeners de teclado para Undo/Redo
 * Usa apenas quando o modo de edição está ativo
 */
export function useUndoRedoShortcuts() {
  const { isEditing, undo, redo, canUndo, canRedo } = useDREStore();

  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Verificar se é Meta (Mac) ou Ctrl (Windows/Linux)
      const isModifierKey = event.metaKey || event.ctrlKey;

      if (!isModifierKey) return;

      // Ctrl+Z (ou Cmd+Z no Mac) - Undo
      if (event.key === 'z' && !event.shiftKey) {
        if (canUndo()) {
          event.preventDefault();
          undo();
        }
      }

      // Ctrl+Shift+Z (ou Cmd+Shift+Z no Mac) - Redo
      // Também aceita Ctrl+Y como alternativa no Windows
      if (
        (event.key === 'z' && event.shiftKey) ||
        (event.key === 'y' && !event.shiftKey)
      ) {
        if (canRedo()) {
          event.preventDefault();
          redo();
        }
      }
    };

    // Adicionar listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, undo, redo, canUndo, canRedo]);
}
